import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useWebSocket, WS_MESSAGE_TYPES } from '@/hooks/useWebSocket';
import { messagesAPI, uploadsAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
    formatRelativeTime,
    formatTime,
    formatDateTime,
    getInitials,
    cn,
} from '@/lib/utils';
import {
    MessageCircle,
    MessageSquare,
    Send,
    ArrowLeft,
    Package,
    User,
    Check,
    CheckCheck,
    PenSquare,
    Image as ImageIcon,
    X,
    Loader2,
} from 'lucide-react';
import { toast } from 'sonner';

const MessagesPage = () => {
    const { conversationId } = useParams();
    const [searchParams] = useSearchParams();
    const { user, isAuthenticated } = useAuth();
    const navigate = useNavigate();
    const [conversations, setConversations] = useState([]);
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [sending, setSending] = useState(false);
    const [newMessage, setNewMessage] = useState('');
    const [activeConversation, setActiveConversation] = useState(null);
    const [showNewMessage, setShowNewMessage] = useState(false);
    const [newRecipient, setNewRecipient] = useState({ id: '', name: '' });
    const [selectedImages, setSelectedImages] = useState([]);
    const [imagePreviews, setImagePreviews] = useState([]);
    const [uploadingImages, setUploadingImages] = useState(false);
    const [isOtherTyping, setIsOtherTyping] = useState(false);
    const [typingUsername, setTypingUsername] = useState('');
    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);
    const fileInputRef = useRef(null);
    const typingTimeoutRef = useRef(null);
    const isTypingRef = useRef(false); // Track if we're currently in "typing" state
    
    // Ref to track the current active conversation for WebSocket handler
    const activeConversationRef = useRef(null);
    const audioContextRef = useRef(null);
    
    // Keep ref in sync with state
    useEffect(() => {
        activeConversationRef.current = activeConversation;
    }, [activeConversation]);
    
    // Play notification beep sound when new message arrives
    const playNotificationBeep = useCallback(() => {
        try {
            // Create or reuse AudioContext
            if (!audioContextRef.current) {
                audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
            }
            const ctx = audioContextRef.current;
            
            // Resume context if suspended (required for user interaction policy)
            if (ctx.state === 'suspended') {
                ctx.resume();
            }
            
            // Create oscillator for beep sound
            const oscillator = ctx.createOscillator();
            const gainNode = ctx.createGain();
            
            oscillator.connect(gainNode);
            gainNode.connect(ctx.destination);
            
            // Configure beep sound - pleasant notification tone
            oscillator.frequency.setValueAtTime(880, ctx.currentTime); // A5 note
            oscillator.type = 'sine';
            
            // Envelope for smooth sound
            gainNode.gain.setValueAtTime(0, ctx.currentTime);
            gainNode.gain.linearRampToValueAtTime(0.3, ctx.currentTime + 0.01);
            gainNode.gain.linearRampToValueAtTime(0, ctx.currentTime + 0.15);
            
            // Play the beep
            oscillator.start(ctx.currentTime);
            oscillator.stop(ctx.currentTime + 0.15);
        } catch (error) {
            console.log('Could not play notification sound:', error);
        }
    }, []);
    
    // Image resize function - client-side to reduce upload size
    const resizeImageFile = async (file, maxSize, maxDimension) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (e) => {
                const img = new Image();
                img.onload = () => {
                    let { width, height } = img;
                    
                    // Calculate new dimensions if needed
                    if (width > maxDimension || height > maxDimension) {
                        if (width > height) {
                            height = Math.round(height * (maxDimension / width));
                            width = maxDimension;
                        } else {
                            width = Math.round(width * (maxDimension / height));
                            height = maxDimension;
                        }
                    }
                    
                    // Create canvas and resize
                    const canvas = document.createElement('canvas');
                    canvas.width = width;
                    canvas.height = height;
                    const ctx = canvas.getContext('2d');
                    ctx.drawImage(img, 0, 0, width, height);
                    
                    // Try different quality levels to get under maxSize
                    let quality = 0.9;
                    const tryCompress = () => {
                        canvas.toBlob((blob) => {
                            if (blob.size <= maxSize || quality <= 0.3) {
                                resolve(new File([blob], file.name, { type: 'image/jpeg' }));
                            } else {
                                quality -= 0.1;
                                tryCompress();
                            }
                        }, 'image/jpeg', quality);
                    };
                    tryCompress();
                };
                img.onerror = reject;
                img.src = e.target.result;
            };
            reader.onerror = reject;
            reader.readAsDataURL(file);
        });
    };
    
    // Handle image selection - up to 10 images
    const handleImageSelect = async (e) => {
        const files = Array.from(e.target.files || []);
        if (!files.length) return;
        
        // Calculate how many more images can be added
        const remainingSlots = 10 - selectedImages.length;
        if (remainingSlots <= 0) {
            toast.error('Poți atașa maximum 10 imagini');
            return;
        }
        
        const filesToAdd = files.slice(0, remainingSlots);
        
        // Validate each file
        const validFiles = [];
        const newPreviews = [];
        
        for (const file of filesToAdd) {
            // Validate file type
            if (!file.type.startsWith('image/')) {
                toast.error(`${file.name} nu este o imagine validă`);
                continue;
            }
            
            // Validate initial size (10MB max for upload)
            if (file.size > 10 * 1024 * 1024) {
                toast.error(`${file.name} este prea mare (max 10MB)`);
                continue;
            }
            
            validFiles.push(file);
            
            // Create preview
            const preview = await new Promise((resolve) => {
                const reader = new FileReader();
                reader.onload = (e) => resolve(e.target.result);
                reader.readAsDataURL(file);
            });
            newPreviews.push(preview);
        }
        
        if (validFiles.length > 0) {
            setSelectedImages(prev => [...prev, ...validFiles]);
            setImagePreviews(prev => [...prev, ...newPreviews]);
            
            if (files.length > remainingSlots) {
                toast.info(`Au fost adăugate ${validFiles.length} imagini (max 10)`);
            }
        }
        
        // Reset file input
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };
    
    // Remove a specific image
    const removeImage = (index) => {
        setSelectedImages(prev => prev.filter((_, i) => i !== index));
        setImagePreviews(prev => prev.filter((_, i) => i !== index));
    };
    
    // Clear all selected images
    const clearSelectedImages = () => {
        setSelectedImages([]);
        setImagePreviews([]);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    // WebSocket for real-time messages
    const { lastMessage } = useWebSocket(user?.user_id);

    // Handle incoming WebSocket messages
    useEffect(() => {
        if (!lastMessage) return;
        
        const { type, data } = lastMessage;
        
        console.log('[MessagesPage] WebSocket message received:', type, data);
        
        // Handle typing indicator - show immediately, hide when stop signal received
        if (type === WS_MESSAGE_TYPES.TYPING) {
            const currentActiveConv = activeConversationRef.current;
            if (currentActiveConv && data.conversation_id === currentActiveConv.conversation_id) {
                if (data.is_typing) {
                    // Start typing - prefer name over username
                    setIsOtherTyping(true);
                    setTypingUsername(data.name || data.username || '');
                } else {
                    // Stop typing
                    setIsOtherTyping(false);
                    setTypingUsername('');
                }
            }
            return;
        }
        
        if (type === WS_MESSAGE_TYPES.NEW_MESSAGE) {
            const currentActiveConv = activeConversationRef.current;
            
            console.log('[MessagesPage] NEW_MESSAGE for conversation:', data.conversation_id);
            console.log('[MessagesPage] Current active conversation:', currentActiveConv?.conversation_id);
            
            // Hide typing indicator when message arrives
            if (currentActiveConv && data.conversation_id === currentActiveConv.conversation_id) {
                setIsOtherTyping(false);
                setTypingUsername('');
            }
            
            // Check if this message belongs to the active conversation
            if (currentActiveConv && data.conversation_id === currentActiveConv.conversation_id) {
                // Add new message to the messages list
                const newMsg = {
                    message_id: data.message_id,
                    conversation_id: data.conversation_id,
                    sender_id: data.sender_id,
                    sender_username: data.sender_username,
                    content: data.content,
                    image_url: data.image_url,
                    created_at: new Date().toISOString(),
                    read: false
                };
                
                console.log('[MessagesPage] Adding message to conversation:', newMsg);
                
                setMessages(prev => {
                    // Avoid duplicates
                    if (prev.some(m => m.message_id === newMsg.message_id)) {
                        console.log('[MessagesPage] Message already exists, skipping');
                        return prev;
                    }
                    console.log('[MessagesPage] Message added, new count:', prev.length + 1);
                    
                    // Don't play beep for active conversation - user is already viewing it
                    // Beep only plays for messages in OTHER conversations (handled below)
                    
                    return [...prev, newMsg];
                });
            }
            
            // Update conversations list with new message
            setConversations(prev => {
                return prev.map(conv => {
                    if (conv.conversation_id === data.conversation_id) {
                        // Play beep for messages from others (even if not in active conversation)
                        if (data.sender_id !== user?.user_id && conv.conversation_id !== currentActiveConv?.conversation_id) {
                            playNotificationBeep();
                        }
                        
                        return {
                            ...conv,
                            last_message: data.content,
                            last_message_at: new Date().toISOString(),
                            unread_count: conv.conversation_id === currentActiveConv?.conversation_id 
                                ? 0 
                                : (conv.unread_count || 0) + 1
                        };
                    }
                    return conv;
                }).sort((a, b) => new Date(b.last_message_at || b.updated_at) - new Date(a.last_message_at || a.updated_at));
            });
        }
    }, [lastMessage, user?.user_id, playNotificationBeep]);
    
    // Send typing indicator - instant, no throttle
    const sendTypingIndicator = useCallback((isTyping) => {
        if (!activeConversation) return;
        
        messagesAPI.sendTyping(activeConversation.conversation_id, isTyping).catch(() => {
            // Silently ignore errors
        });
    }, [activeConversation]);
    
    // Continuous typing indicator - refresh every 3 seconds while typing
    useEffect(() => {
        if (!isTypingRef.current || !activeConversation) return;
        
        const refreshTyping = setInterval(() => {
            if (isTypingRef.current && activeConversation) {
                messagesAPI.sendTyping(activeConversation.conversation_id, true).catch(() => {});
            }
        }, 3000); // Refresh every 3 seconds
        
        return () => clearInterval(refreshTyping);
    }, [activeConversation]);
    
    // Handle input change with typing indicator
    const handleInputChange = (e) => {
        const value = e.target.value;
        setNewMessage(value);
        
        const hasText = value.trim().length > 0;
        const wasTyping = isTypingRef.current;
        
        // Send start typing when first character is typed
        if (hasText && !wasTyping) {
            isTypingRef.current = true;
            sendTypingIndicator(true);
        }
        // Send stop typing when text is cleared
        else if (!hasText && wasTyping) {
            isTypingRef.current = false;
            sendTypingIndicator(false);
        }
    };
    
    // Reset typing state when conversation changes or component unmounts
    useEffect(() => {
        return () => {
            if (isTypingRef.current && activeConversation) {
                // Send stop typing when leaving
                messagesAPI.sendTyping(activeConversation.conversation_id, false).catch(() => {});
                isTypingRef.current = false;
            }
        };
    }, [activeConversation]);

    // Check for recipient param (from order page "Trimite mesaj")
    useEffect(() => {
        const recipientId = searchParams.get('recipient');
        const recipientName = searchParams.get('name');
        if (recipientId) {
            setNewRecipient({ id: recipientId, name: recipientName || '' });
            setShowNewMessage(true);
        }
    }, [searchParams]);

    useEffect(() => {
        if (!isAuthenticated) {
            navigate('/login');
            return;
        }
        fetchConversations();
    }, [isAuthenticated, navigate]);

    useEffect(() => {
        if (conversationId) {
            fetchMessages(conversationId);
        } else {
            // When navigating back to /messages (no conversationId), clear active conversation
            setActiveConversation(null);
            setMessages([]);
        }
    }, [conversationId]);

    useEffect(() => {
        // Scroll to bottom when messages change
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    // Polling fallback - always enabled when in a conversation for reliability
    useEffect(() => {
        // Only poll when viewing a specific conversation
        if (!conversationId) return;
        
        console.log('[MessagesPage] Enabling polling for conversation:', conversationId);
        
        // Track last known message count and typing state per conversation
        let lastTypingCheck = Date.now();
        
        const pollData = async () => {
            try {
                // Poll messages for this conversation
                const msgRes = await messagesAPI.getMessages(conversationId);
                const newMessages = msgRes.data;
                
                setMessages(prev => {
                    // Always update if we have more messages or different last message
                    if (newMessages.length > prev.length) {
                        console.log('[MessagesPage] Polling found new messages:', newMessages.length - prev.length);
                        // Don't play beep - user is viewing this conversation
                        return newMessages;
                    }
                    // Check if last message is different
                    if (newMessages.length > 0 && prev.length > 0) {
                        const lastNew = newMessages[newMessages.length - 1];
                        const lastPrev = prev[prev.length - 1];
                        if (lastNew.message_id !== lastPrev.message_id) {
                            console.log('[MessagesPage] Polling found updated messages');
                            // Don't play beep - user is viewing this conversation
                            return newMessages;
                        }
                    }
                    return prev;
                });
                
                // Poll typing status every 2 seconds
                if (Date.now() - lastTypingCheck >= 2000) {
                    lastTypingCheck = Date.now();
                    try {
                        const typingRes = await messagesAPI.getTypingStatus(conversationId);
                        if (typingRes.data) {
                            const { is_typing, name, username } = typingRes.data;
                            setIsOtherTyping(is_typing || false);
                            setTypingUsername(name || username || '');
                        }
                    } catch (e) {
                        // Ignore typing status errors
                    }
                }
            } catch (error) {
                // Silently ignore polling errors
                console.log('[MessagesPage] Polling error (ignored):', error.message);
            }
        };
        
        // Poll every 2 seconds for better responsiveness
        const pollInterval = setInterval(pollData, 2000);
        
        // Also poll immediately on mount
        pollData();
        
        return () => clearInterval(pollInterval);
    }, [conversationId]);
    
    // Poll conversations list in background (for unread badges)
    useEffect(() => {
        const pollConversations = async () => {
            try {
                const convRes = await messagesAPI.getConversations();
                const newConversations = convRes.data;
                
                setConversations(prev => {
                    const hasChanges = newConversations.some(newConv => {
                        const oldConv = prev.find(c => c.conversation_id === newConv.conversation_id);
                        if (!oldConv) return true;
                        return oldConv.last_message_at !== newConv.last_message_at || 
                               oldConv.unread_count !== newConv.unread_count;
                    });
                    
                    if (hasChanges) {
                        console.log('[MessagesPage] Polling found conversation updates');
                        return newConversations;
                    }
                    return prev;
                });
            } catch (error) {
                // Silently ignore
            }
        };
        
        // Poll conversations every 5 seconds
        const pollInterval = setInterval(pollConversations, 5000);
        
        return () => clearInterval(pollInterval);
    }, []);

    const fetchConversations = async () => {
        try {
            const res = await messagesAPI.getConversations();
            setConversations(res.data);
            
            // If we have a conversationId, set active conversation
            if (conversationId) {
                const active = res.data.find(c => c.conversation_id === conversationId);
                setActiveConversation(active);
            }
        } catch (error) {
            console.error('Error fetching conversations:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchMessages = async (convId) => {
        try {
            const res = await messagesAPI.getMessages(convId);
            setMessages(res.data);
            
            // Update active conversation
            const active = conversations.find(c => c.conversation_id === convId);
            if (active) {
                setActiveConversation(active);
                // Reset unread count for this conversation in local state
                setConversations(prev => prev.map(c => 
                    c.conversation_id === convId ? { ...c, unread_count: 0 } : c
                ));
            }
            
            // Mark all messages in this conversation as read (backend)
            try {
                await messagesAPI.markConversationRead(convId);
            } catch (e) {
                // Silently ignore - local state already updated
            }
            
            // Focus input after loading messages
            setTimeout(() => inputRef.current?.focus(), 100);
        } catch (error) {
            console.error('Error fetching messages:', error);
        }
    };

    const handleSendMessage = async (e) => {
        // Prevent form submission default behavior which causes keyboard to dismiss
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        
        // Immediately refocus to prevent keyboard from closing on mobile
        requestAnimationFrame(() => {
            inputRef.current?.focus();
        });
        
        if ((!newMessage.trim() && selectedImages.length === 0) || !activeConversation) return;

        const messageContent = newMessage.trim();
        const tempMessageId = `temp_${Date.now()}`;
        let imageUrls = [];
        
        // If there are images, upload them first
        if (selectedImages.length > 0) {
            setUploadingImages(true);
            try {
                // Upload all images in parallel
                const uploadPromises = selectedImages.map(async (image) => {
                    const resizedImage = await resizeImageFile(image, 2 * 1024 * 1024, 1920);
                    const formData = new FormData();
                    formData.append('file', resizedImage);
                    const uploadRes = await uploadsAPI.uploadImage(formData);
                    return uploadRes.data?.url;
                });
                
                imageUrls = (await Promise.all(uploadPromises)).filter(Boolean);
                
                if (imageUrls.length === 0) {
                    toast.error('Eroare la încărcarea imaginilor');
                    setUploadingImages(false);
                    return;
                }
            } catch (error) {
                console.error('Error uploading images:', error);
                toast.error('Eroare la încărcarea imaginilor');
                setUploadingImages(false);
                return;
            }
            setUploadingImages(false);
        }
        
        // Optimistically add message to UI
        const optimisticMsg = {
            message_id: tempMessageId,
            conversation_id: activeConversation.conversation_id,
            sender_id: user?.user_id,
            sender_username: user?.username,
            content: messageContent,
            image_url: imageUrls[0] || imagePreviews[0],
            image_urls: imageUrls.length > 0 ? imageUrls : (imagePreviews.length > 0 ? imagePreviews : null),
            created_at: new Date().toISOString(),
            read: false
        };
        
        setMessages(prev => [...prev, optimisticMsg]);
        setNewMessage('');
        setSelectedImages([]);
        setImagePreviews([]);
        // Don't call focus here - it should stay on input naturally

        setSending(true);
        try {
            const response = await messagesAPI.send({
                conversation_id: activeConversation.conversation_id,
                content: messageContent,
                image_urls: imageUrls.length > 0 ? imageUrls : undefined
            });
            
            // Replace temp message with real one
            if (response?.data?.message_id) {
                setMessages(prev => prev.map(m => 
                    m.message_id === tempMessageId 
                        ? { ...m, message_id: response.data.message_id, image_url: response.data.image_url, image_urls: response.data.image_urls }
                        : m
                ));
            }
        } catch (error) {
            // Remove optimistic message on error
            setMessages(prev => prev.filter(m => m.message_id !== tempMessageId));
            setNewMessage(messageContent);
            toast.error('Eroare la trimiterea mesajului');
        } finally {
            setSending(false);
        }
    };

    const handleSendNewMessage = async (e) => {
        e.preventDefault();
        if (!newMessage.trim() || !newRecipient.id) return;

        setSending(true);
        try {
            // Create or get conversation and send message
            const res = await messagesAPI.sendNew({
                recipient_id: newRecipient.id,
                content: newMessage.trim()
            });
            setNewMessage('');
            setShowNewMessage(false);
            setNewRecipient({ id: '', name: '' });
            // Clear URL params
            navigate('/messages', { replace: true });
            // Refresh conversations and open the new one
            await fetchConversations();
            if (res.data?.conversation_id) {
                navigate(`/messages/${res.data.conversation_id}`);
            }
            toast.success('Mesaj trimis!');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Eroare la trimiterea mesajului');
        } finally {
            setSending(false);
        }
    };

    const selectConversation = (conv) => {
        setActiveConversation(conv);
        navigate(`/messages/${conv.conversation_id}`);
        // Focus input after selecting conversation
        setTimeout(() => inputRef.current?.focus(), 100);
    };

    const getOtherParticipant = (conv) => {
        // Get the other participant's info from the conversation
        const participants = conv.participants || [];
        const participantUsernames = conv.participant_usernames || [];
        const participantNames = conv.participant_names || [];
        const myUserId = user?.user_id;
        
        // Find the index of the other participant
        const otherIndex = participants.findIndex(p => p !== myUserId);
        const idx = otherIndex >= 0 ? otherIndex : 0;
        
        return {
            user_id: participants[idx],
            username: participantUsernames[idx] || 'Unknown',
            name: participantNames[idx] || participantUsernames[idx] || 'Unknown'
        };
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-background py-8">
                <div className="container mx-auto px-4 lg:px-8">
                    <div className="grid lg:grid-cols-3 gap-6 h-[calc(100vh-200px)]">
                        <Skeleton className="rounded-2xl" />
                        <Skeleton className="lg:col-span-2 rounded-2xl" />
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="fixed inset-0 top-[48px] md:top-[64px] bg-background overflow-hidden" data-testid="messages-page">
            <div className="container mx-auto px-2 md:px-4 lg:px-8 h-full">
                <div className="grid lg:grid-cols-3 gap-4 h-full">
                    {/* Conversations List - hidden on mobile when conversation is active */}
                    <Card className={cn(
                        "rounded-2xl flex flex-col overflow-hidden",
                        activeConversation ? "hidden lg:flex" : "flex"
                    )}>
                        <CardHeader className="border-b py-2 shrink-0">
                            <CardTitle className="text-base flex items-center gap-2">
                                <MessageCircle className="w-4 h-4" />
                                Conversații
                            </CardTitle>
                        </CardHeader>
                        <ScrollArea className="flex-1 min-h-0">
                            {conversations.length === 0 ? (
                                <div className="p-4 text-center">
                                    <MessageCircle className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                                    <p className="text-muted-foreground text-sm">Nu ai conversații încă</p>
                                    <p className="text-xs text-muted-foreground mt-1">
                                        Contactează un vânzător pentru a începe
                                    </p>
                                </div>
                            ) : (
                                <div className="divide-y">
                                    {conversations.map(conv => {
                                        const other = getOtherParticipant(conv);
                                        const isActive = activeConversation?.conversation_id === conv.conversation_id;
                                        
                                        return (
                                            <button
                                                key={conv.conversation_id}
                                                onClick={() => selectConversation(conv)}
                                                className={cn(
                                                    "w-full p-3 text-left hover:bg-gray-50 transition-colors",
                                                    isActive && "bg-blue-50 hover:bg-blue-50"
                                                )}
                                            >
                                                <div className="flex items-start gap-3">
                                                    <Avatar className="w-10 h-10 shrink-0">
                                                        <AvatarFallback className="bg-primary text-white text-sm">
                                                            {getInitials(other?.name || other?.username)}
                                                        </AvatarFallback>
                                                    </Avatar>
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex items-center justify-between">
                                                            <p className="font-semibold text-sm truncate">{other?.name || other?.username}</p>
                                                            {conv.unread_count > 0 && (
                                                                <Badge className="bg-primary text-white text-xs ml-2">
                                                                    {conv.unread_count}
                                                                </Badge>
                                                            )}
                                                        </div>
                                                        {conv.listing_title && (
                                                            <p className="text-xs text-primary truncate flex items-center gap-1">
                                                                {conv.conversation_type === 'support' ? (
                                                                    <MessageSquare className="w-3 h-3" />
                                                                ) : (
                                                                    <Package className="w-3 h-3" />
                                                                )}
                                                                {conv.listing_title}
                                                            </p>
                                                        )}
                                                        {conv.last_message && (
                                                            <p className="text-sm text-muted-foreground truncate mt-0.5">
                                                                {typeof conv.last_message === 'string' 
                                                                    ? conv.last_message 
                                                                    : conv.last_message.content}
                                                            </p>
                                                        )}
                                                        <p className="text-xs text-muted-foreground mt-0.5">
                                                            {formatRelativeTime(conv.last_message_at || conv.updated_at)}
                                                        </p>
                                                    </div>
                                                </div>
                                            </button>
                                        );
                                    })}
                                </div>
                            )}
                        </ScrollArea>
                    </Card>

                    {/* Chat Area - full screen on mobile when active */}
                    <Card className={cn(
                        "rounded-2xl overflow-hidden flex flex-col",
                        activeConversation ? "lg:col-span-2" : "lg:col-span-2 hidden lg:flex"
                    )}>
                        {showNewMessage ? (
                            /* New Message Form */
                            <>
                                <CardHeader className="border-b py-3">
                                    <div className="flex items-center gap-3">
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            className="rounded-full"
                                            onClick={() => {
                                                setShowNewMessage(false);
                                                setNewRecipient({ id: '', name: '' });
                                                navigate('/messages', { replace: true });
                                            }}
                                        >
                                            <ArrowLeft className="w-5 h-5" />
                                        </Button>
                                        <div className="flex items-center gap-2">
                                            <PenSquare className="w-5 h-5 text-primary" />
                                            <div>
                                                <p className="font-semibold">Mesaj nou</p>
                                                <p className="text-sm text-muted-foreground">
                                                    Către: {newRecipient.name || newRecipient.id}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </CardHeader>

                                <div className="flex-1 flex flex-col p-4">
                                    <div className="flex-1 flex items-center justify-center">
                                        <div className="text-center w-full max-w-md">
                                            <Avatar className="w-14 h-14 mx-auto mb-3">
                                                <AvatarFallback className="bg-primary text-white text-xl">
                                                    {getInitials(newRecipient.name)}
                                                </AvatarFallback>
                                            </Avatar>
                                            <h3 className="font-semibold text-lg mb-1">{newRecipient.name}</h3>
                                            <p className="text-muted-foreground mb-6">
                                                Trimite un mesaj pentru a începe conversația
                                            </p>
                                        </div>
                                    </div>

                                    <form onSubmit={handleSendNewMessage} className="mt-auto">
                                        <div className="flex gap-2">
                                            <Textarea
                                                value={newMessage}
                                                onChange={(e) => setNewMessage(e.target.value)}
                                                placeholder="Scrie mesajul tău..."
                                                className="rounded-xl min-h-[80px]"
                                                disabled={sending}
                                                data-testid="new-message-textarea"
                                            />
                                        </div>
                                        <Button 
                                            type="submit" 
                                            className="w-full mt-3 rounded-full bg-primary hover:bg-primary/90"
                                            disabled={!newMessage.trim() || sending}
                                            data-testid="send-new-message-btn"
                                        >
                                            {sending ? (
                                                'Se trimite...'
                                            ) : (
                                                <>
                                                    <Send className="w-4 h-4 mr-2" />
                                                    Trimite mesaj
                                                </>
                                            )}
                                        </Button>
                                    </form>
                                </div>
                            </>
                        ) : activeConversation ? (
                            <>
                                {/* Chat Header */}
                                <CardHeader className="border-b py-1.5 md:py-3">
                                    <div className="flex items-center gap-2 md:gap-3">
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            className="lg:hidden rounded-full h-8 w-8"
                                            onClick={() => {
                                                setActiveConversation(null);
                                                navigate('/messages', { replace: true });
                                            }}
                                        >
                                            <ArrowLeft className="w-4 h-4" />
                                        </Button>
                                        <Avatar className="h-8 w-8 md:h-10 md:w-10">
                                            <AvatarFallback className="bg-primary text-white text-sm md:text-base">
                                                {getInitials(getOtherParticipant(activeConversation)?.name || getOtherParticipant(activeConversation)?.username)}
                                            </AvatarFallback>
                                        </Avatar>
                                        <div className="min-w-0 flex-1">
                                            <p className="font-semibold text-sm md:text-base truncate">
                                                {getOtherParticipant(activeConversation)?.name || getOtherParticipant(activeConversation)?.username}
                                            </p>
                                            {activeConversation.listing_title && (
                                                activeConversation.conversation_type === 'support' ? (
                                                    <p className="text-xs md:text-sm text-primary flex items-center gap-1 truncate">
                                                        <MessageSquare className="w-3 h-3 shrink-0" />
                                                        <span className="truncate">{activeConversation.listing_title}</span>
                                                    </p>
                                                ) : (
                                                    <Link 
                                                        to={`/listing/${activeConversation.listing_slug || activeConversation.listing_id}`}
                                                        className="text-xs md:text-sm text-primary hover:underline flex items-center gap-1 truncate"
                                                    >
                                                        <Package className="w-3 h-3 shrink-0" />
                                                        <span className="truncate">{activeConversation.listing_title}</span>
                                                    </Link>
                                                )
                                            )}
                                        </div>
                                    </div>
                                </CardHeader>

                                {/* Messages */}
                                <ScrollArea className="flex-1 p-2 md:p-3">
                                    <div className="space-y-2 md:space-y-3">
                                        {messages.map(msg => {
                                            const isOwn = msg.sender_id === user?.user_id;
                                            
                                            return (
                                                <div
                                                    key={msg.message_id}
                                                    className={cn(
                                                        "flex flex-col w-full",
                                                        isOwn ? "items-end" : "items-start"
                                                    )}
                                                >
                                                    <div
                                                        className={cn(
                                                            "max-w-[85%] md:max-w-[70%] rounded-2xl overflow-hidden",
                                                            isOwn 
                                                                ? "bg-primary text-white rounded-br-md" 
                                                                : "bg-gray-100 text-foreground rounded-bl-md",
                                                            (msg.image_url || msg.image_urls?.length) && !msg.content ? "p-0" : "px-3 py-2 md:px-4"
                                                        )}
                                                    >
                                                        {/* Display multiple images or single image */}
                                                        {(msg.image_urls?.length > 0 || msg.image_url) && (
                                                            <div className={cn(
                                                                "grid gap-1",
                                                                msg.content ? "mx-2 mt-2 mb-2" : "",
                                                                msg.image_urls?.length > 1 ? (
                                                                    msg.image_urls.length === 2 ? "grid-cols-2" :
                                                                    msg.image_urls.length <= 4 ? "grid-cols-2" :
                                                                    "grid-cols-3"
                                                                ) : ""
                                                            )}>
                                                                {(msg.image_urls || [msg.image_url]).filter(Boolean).map((imgUrl, imgIdx) => (
                                                                    <a 
                                                                        key={imgIdx}
                                                                        href={imgUrl} 
                                                                        target="_blank" 
                                                                        rel="noopener noreferrer"
                                                                        className="block"
                                                                    >
                                                                        <img 
                                                                            src={imgUrl} 
                                                                            alt={`Imagine ${imgIdx + 1}`}
                                                                            className={cn(
                                                                                "w-full rounded-lg cursor-pointer hover:opacity-90 transition-opacity object-cover",
                                                                                (msg.image_urls?.length || 1) > 1 ? "h-24 md:h-32" : ""
                                                                            )}
                                                                            style={{ 
                                                                                maxHeight: (msg.image_urls?.length || 1) > 1 ? undefined : '300px', 
                                                                                objectFit: 'cover' 
                                                                            }}
                                                                        />
                                                                    </a>
                                                                ))}
                                                            </div>
                                                        )}
                                                        {msg.content && (
                                                            <p className={cn(
                                                                "whitespace-pre-wrap wrap-break-word text-sm md:text-base",
                                                                (msg.image_url || msg.image_urls?.length) ? "px-2 pb-2" : ""
                                                            )}>{msg.content}</p>
                                                        )}
                                                    </div>
                                                    {/* Timestamp below message */}
                                                    <span className={cn(
                                                        "text-[10px] md:text-xs text-muted-foreground mt-1",
                                                        isOwn ? "mr-1" : "ml-1"
                                                    )}>
                                                        {formatDateTime(msg.created_at)}
                                                    </span>
                                                </div>
                                            );
                                        })}
                                        <div ref={messagesEndRef} />
                                    </div>
                                </ScrollArea>

                                {/* Typing Indicator */}
                                {isOtherTyping && (
                                    <div className="px-4 py-2 border-t bg-gray-50/50">
                                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                            <div className="flex gap-1">
                                                <span className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                                                <span className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                                                <span className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                                            </div>
                                            <span>{typingUsername} scrie...</span>
                                        </div>
                                    </div>
                                )}

                                {/* Image Previews - Multiple */}
                                {imagePreviews.length > 0 && (
                                    <div className="px-3 pt-2 border-t bg-gray-50">
                                        <div className="flex items-center gap-2 mb-2">
                                            <span className="text-xs text-muted-foreground">
                                                {imagePreviews.length}/10 imagini selectate
                                            </span>
                                            <button
                                                type="button"
                                                onClick={clearSelectedImages}
                                                className="text-xs text-red-500 hover:text-red-600"
                                            >
                                                Șterge toate
                                            </button>
                                        </div>
                                        <div className="flex flex-wrap gap-2">
                                            {imagePreviews.map((preview, idx) => (
                                                <div key={idx} className="relative">
                                                    <img 
                                                        src={preview} 
                                                        alt={`Preview ${idx + 1}`} 
                                                        className="h-16 w-16 rounded-lg object-cover"
                                                    />
                                                    <button
                                                        type="button"
                                                        onClick={() => removeImage(idx)}
                                                        className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full p-0.5 hover:bg-red-600 transition-colors"
                                                    >
                                                        <X className="w-3 h-3" />
                                                    </button>
                                                </div>
                                            ))}
                                            {imagePreviews.length < 10 && (
                                                <button
                                                    type="button"
                                                    onClick={() => fileInputRef.current?.click()}
                                                    className="h-16 w-16 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center hover:border-primary hover:bg-primary/5 transition-colors"
                                                >
                                                    <ImageIcon className="w-5 h-5 text-gray-400" />
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {/* Input */}
                                <form 
                                    onSubmit={handleSendMessage} 
                                    className="p-2 md:p-3 border-t"
                                    action="javascript:void(0)"
                                >
                                    <input
                                        type="file"
                                        ref={fileInputRef}
                                        onChange={handleImageSelect}
                                        accept="image/*"
                                        multiple
                                        className="hidden"
                                    />
                                    <div className="flex gap-2">
                                        <Button
                                            type="button"
                                            variant="outline"
                                            size="icon"
                                            className={cn(
                                                "rounded-full shrink-0 relative",
                                                selectedImages.length > 0 && "border-primary text-primary"
                                            )}
                                            onClick={() => fileInputRef.current?.click()}
                                            disabled={sending || uploadingImages || selectedImages.length >= 10}
                                            tabIndex={-1}
                                            data-testid="attach-image-btn"
                                        >
                                            {uploadingImages ? (
                                                <Loader2 className="w-4 h-4 animate-spin" />
                                            ) : (
                                                <ImageIcon className="w-4 h-4" />
                                            )}
                                            {selectedImages.length > 0 && (
                                                <span className="absolute -top-1 -right-1 bg-primary text-white text-[10px] rounded-full w-4 h-4 flex items-center justify-center">
                                                    {selectedImages.length}
                                                </span>
                                            )}
                                        </Button>
                                        <Input
                                            ref={inputRef}
                                            value={newMessage}
                                            onChange={handleInputChange}
                                            placeholder="Scrie un mesaj..."
                                            className="rounded-full"
                                            readOnly={uploadingImages}
                                            autoComplete="off"
                                            enterKeyHint="send"
                                            data-testid="message-input"
                                        />
                                        <Button 
                                            type="button"
                                            size="icon"
                                            className="rounded-full bg-primary hover:bg-primary/90"
                                            disabled={(!newMessage.trim() && selectedImages.length === 0) || sending || uploadingImages}
                                            tabIndex={-1}
                                            onMouseDown={(e) => e.preventDefault()}
                                            onTouchStart={(e) => e.preventDefault()}
                                            onClick={(e) => {
                                                e.preventDefault();
                                                handleSendMessage(e);
                                            }}
                                            data-testid="send-message-btn"
                                        >
                                            {uploadingImages ? (
                                                <Loader2 className="w-4 h-4 animate-spin" />
                                            ) : (
                                                <Send className="w-4 h-4" />
                                            )}
                                        </Button>
                                    </div>
                                </form>
                            </>
                        ) : (
                            <div className="flex-1 flex items-center justify-center">
                                <div className="text-center">
                                    <MessageCircle className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                                    <h3 className="font-semibold mb-2">Selectează o conversație</h3>
                                    <p className="text-sm text-muted-foreground">
                                        Alege o conversație din stânga pentru a vedea mesajele
                                    </p>
                                </div>
                            </div>
                        )}
                    </Card>
                </div>
            </div>
        </div>
    );
};

export default MessagesPage;
